import React from 'react';
import PropTypes from 'prop-types';
import {
  useQuery,
  NetworkStatus,
  usePaginationHelper,
  useNavigate,
} from '@common/hooks';
import { Space } from 'antd';
import { Colors, FontSizes, Spacing } from 'works/legacy/lib/style-helper';
import moment from 'moment';
import { RoleShift } from '@scheduling/components';
import { FilteredTable } from 'works/components/filtered-table';
import { AssignmentStateMenu } from 'works/components/assignment-state-menu';
import { AssignmentCancelReason } from '@working-experience/components';
import { ExtensionOfferState } from 'works/components/extension-offer-state';
import { PaginatedStaffingRequestsAssignmentsQuery } from 'works/graphql/staffing-request-assignment';
import {
  Currency,
  DateRangeFilter,
  ErrorHandler,
  LoadingState,
  NumberRangeFilter,
} from '@common/components';
import { ActionableTableCell } from './index';
import {
  WORKS_CLINICAL_UNITS_QUERY,
  ORGANIZATION_BUSINESSES_QUERY,
} from '@institutions/graphql';

export function WorkerAssignmentsTable({ workerId }) {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'WorkerAssignmentsTable',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'STATUS',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      startDate: 'START',
      endDate: 'END',
      state: 'STATUS',
      costCenter: 'COST_CENTER',
      rate: 'RATE',
      supplierName: 'SUPPLIER_NAME',
      shift: 'SHIFT',
      hiringLeader: 'HIRING_LEADER',
      code: 'CODE',
      clinicalUnit: 'CLINICAL_UNIT',
      externalId: 'EXTERNAL_ID',
    },
  });

  const navigate = useNavigate();

  const {
    loading: orgBusinessLoading,
    error: orgBusinesserror,
    data: orgBusinessData,
  } = useQuery(ORGANIZATION_BUSINESSES_QUERY);

  const {
    loading: clinicalUnitLoading,
    error: clinicalUnitError,
    data: clinicalUnitData,
  } = useQuery(WORKS_CLINICAL_UNITS_QUERY);

  const { error, data, networkStatus } = useQuery(
    PaginatedStaffingRequestsAssignmentsQuery,
    {
      variables: {
        ...paginationVariables,
        staffingRequestTypes: ['on_demand'],
        filters: {
          ...paginationVariables.filters,
          workerId: [workerId],
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  if (orgBusinessLoading || clinicalUnitLoading) {
    return <LoadingState />;
  }

  const anyError = error || orgBusinesserror || clinicalUnitError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="WorkerAssignmentsTable" />;
  }

  const isLoaded = Boolean(
    networkStatus === NetworkStatus.ready &&
    data &&
    orgBusinessData &&
    clinicalUnitData
  );
  const assignments = isLoaded
    ? data.paginatedStaffingRequestAssignments.staffingRequestAssignments
    : [];
  const total = isLoaded
    ? data.paginatedStaffingRequestAssignments.totalCount
    : null;

  const onCellClick = (record) => {
    navigate(`assignments/${record.id}`);
  };

  const columns = [
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplier',
      sorter: true,
      render: (_, record) => (
        <ActionableTableCell
          onClick={() => onCellClick(record)}
          customStyles={{ display: 'flex', alignItems: 'center' }}
        >
          {record.supplier.name}
        </ActionableTableCell>
      ),
    },
    {
      title: 'External ID',
      dataIndex: 'externalId',
      key: 'externalId',
      sorter: true,
      render: (_, record) => (
        <ActionableTableCell
          onClick={() => onCellClick(record)}
          customStyles={{ display: 'flex', alignItems: 'center' }}
        >
          {record.staffingRequest.externalId}
        </ActionableTableCell>
      ),
    },
    {
      title: 'Hiring Leader',
      dataIndex: 'hiringLeader',
      key: 'hiringLeader',
      render: (_, record) => (
        <ActionableTableCell
          onClick={() => onCellClick(record)}
          customStyles={{ display: 'flex', alignItems: 'center' }}
        >
          {record.staffingRequest.hiringLeader.name}
        </ActionableTableCell>
      ),
    },
    {
      title: 'CC',
      dataIndex: 'organizationUnit',
      key: 'costCenter',
      sorter: true,
      render: function renderCostCenter(_, record) {
        return (
          <ActionableTableCell
            onClick={() => onCellClick(record)}
            customStyles={{ display: 'flex', alignItems: 'center' }}
          >
            {record.staffingRequest.organizationUnit.costCenter}
          </ActionableTableCell>
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'organizationUnit',
      key: 'clinicalUnit',
      render: function renderClinicalUnit(_, record) {
        let staffingRequest = record.staffingRequest;
        return (
          <ActionableTableCell
            onClick={() => onCellClick(record)}
            customStyles={{ height: '100%' }}
          >
            {staffingRequest.organizationUnit.clinicalUnit.displayName}
            <br />
            <span className="secondary-paragraph">
              {staffingRequest.organizationBusiness.name}
            </span>
          </ActionableTableCell>
        );
      },
    },
    {
      title: 'Role, Shift',
      dataIndex: 'staffingRequestAssignment',
      key: 'shift',
      sorter: true,
      render: function renderStatus(_, assignment) {
        return (
          <ActionableTableCell
            onClick={() => onCellClick(assignment)}
            customStyles={{ display: 'flex', alignItems: 'center' }}
          >
            <RoleShift
              role={assignment.staffingRequest.role.name}
              shiftType={assignment.shiftType}
              shiftsPerWeek={assignment.shiftsPerWeek}
              hoursPerShift={assignment.hoursPerShift}
            />
          </ActionableTableCell>
        );
      },
    },
    {
      title: 'Rate',
      dataIndex: 'billingRegularRate',
      key: 'rate',
      sorter: true,
      filtered: filters.rate,
      filteredValue: filters.rate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (rate, record) => (
        <ActionableTableCell
          onClick={() => onCellClick(record)}
          customStyles={{ display: 'flex', alignItems: 'center' }}
        >
          <Currency value={rate} />
        </ActionableTableCell>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderStartDate(_, assignment) {
        const displayDate = moment(assignment.originalStartDate);
        return (
          <ActionableTableCell
            onClick={() => onCellClick(assignment)}
            customStyles={{ display: 'flex', alignItems: 'center' }}
          >
            {displayDate.format('MM/DD/YYYY')}
          </ActionableTableCell>
        );
      },
    },
    {
      title: 'End Date',
      dataIndex: 'finalEndDate',
      sorter: true,
      key: 'endDate',
      filterFormat: 'dateRange',
      filtered: filters.endDate || false,
      filteredValue: filters.endDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderEndDate(finalEndDate, assignment) {
        const hasStarted = !assignment.neverStarted;
        const isCancelled = assignment.state === 'cancelled';
        const color = hasStarted && isCancelled ? Colors.coral : 'inherit';

        const displayDate = moment(finalEndDate);
        return (
          <ActionableTableCell
            onClick={() => onCellClick(assignment)}
            customStyles={{ display: 'flex', alignItems: 'center' }}
          >
            <span style={{ color }}>{displayDate.format('MM/DD/YYYY')}</span>
          </ActionableTableCell>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'staffingRequestAssignment',
      key: 'state',
      sorter: true,
      defaultSortOrder: 'ascend',
      filtered: filters.state || false,
      filteredValue: filters.state || null,
      filters: [
        {
          text: 'Onboarding',
          value: 'onboarding',
        },
        {
          text: 'Cleared',
          value: 'cleared',
        },
        {
          text: 'Working',
          value: 'working',
        },
        {
          text: 'Completed',
          value: 'completed',
        },
        {
          text: 'Cancelled',
          value: 'cancelled',
        },
        {
          text: 'Extension Pending Approval',
          value: 'extension_pending',
        },
        {
          text: 'Extension Sent',
          value: 'extension_sent',
        },
        {
          text: 'Extended',
          value: 'extended',
        },
      ],
      render: function renderStatus(state, assignment) {
        return (
          <>
            <Space size={4}>
              <AssignmentStateMenu
                assignment={assignment}
                fontSize={FontSizes.small}
              />
              <ExtensionOfferState
                assignment={assignment}
                offer={assignment.activeExtensionOffer}
                extensionCount={assignment.extensionCount}
                fontSize={FontSizes.small}
              />
            </Space>
            {assignment.state === 'cancelled' && (
              <AssignmentCancelReason
                reasonName={assignment.cancelledReason}
                details={assignment.cancelledDetail}
              />
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <FilteredTable
        rowKey="id"
        columns={columns}
        dataSource={assignments}
        loading={!isLoaded}
        pagination={{
          ...paginationVariables,
          total,
        }}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        localStorageFilterKey={localStorageFilterKey}
      />
      <style jsx global>{`
        .ant-table-row.non-compliant-row {
          color: ${Colors.coral};
          background-color: ${Colors.coralHyperlight};
        }

        .ant-table-row.non-compliant-row a {
          color: ${Colors.coral};
        }
        .ant-table-row.non-compliant-row td {
          background-color: ${Colors.coralHyperlight};
        }
      `}</style>
    </>
  );
}

WorkerAssignmentsTable.propTypes = {
  workerId: PropTypes.string,
};
